import { definitions } from '@luxuryescapes/contract-trip'
import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'

import { PublicTripKeys } from '../reactQueryKeys'

import noop from 'lib/function/noop'
import {
  getPublicTrip,
  createPublicId,
  deletePublicTripId,
  fetchPublicId,
  copyPublicTrip,
} from 'tripPlanner/api/publicTrip'
import {
  CopyPublicTripError,
  GetFullPublicTripError,
  CreatePublicIdError,
} from 'tripPlanner/api/publicTrip/types'
import * as Mapper from 'tripPlanner/mappers'
import { FullTrip } from 'tripPlanner/types/common'
import { PublicFullTrip } from 'tripPlanner/types/publicTrip'

export const usePublicTrip = (
  tripId: string,
  options?: UseQueryOptions<
    definitions['fullPublicTrip'],
    GetFullPublicTripError,
    PublicFullTrip
  >,
): UseQueryResult<PublicFullTrip, GetFullPublicTripError> => {
  return useQuery({
    queryKey: PublicTripKeys.detail(tripId),
    queryFn: () => getPublicTrip(tripId),
    select: Mapper.fullPublicTrip,
    ...options,
  })
}

export const useCreatePublicId = (
  options: UseMutationOptions<string, CreatePublicIdError, string>,
) => {
  return useMutation(createPublicId, {
    ...options,
  })
}

export const usePublicTripId = (
  tripId: string,
  enablePublicOption: () => void = noop,
) => {
  const queryClient = useQueryClient()

  const { data: publicTripId, ...result } = useQuery(
    PublicTripKeys.publicTripId(tripId),
    () => fetchPublicId(tripId),
    {
      onSuccess: (data) => {
        if (data) {
          enablePublicOption()
        }
      },
    },
  )

  const { mutate: createPublicId } = useCreatePublicId({
    onSuccess: (id) => {
      queryClient.setQueryData(PublicTripKeys.publicTripId(tripId), id)
    },
  })

  const { mutate: deletePublicId } = useMutation(deletePublicTripId, {
    onSuccess: () => {
      queryClient.setQueryData(PublicTripKeys.publicTripId(tripId), null)
    },
  })

  return {
    ...result,
    publicTripId,
    createPublicId,
    deletePublicId,
  }
}

type CopyArgs = {
  name: string
  tripItemIds?: string[]
}

export const usePublicTripCopy = (
  publicTripId: string,
  regionCode: string,
  options?: UseMutationOptions<FullTrip, CopyPublicTripError, CopyArgs>,
): UseMutationResult<FullTrip, CopyPublicTripError, CopyArgs> => {
  return useMutation({
    mutationFn: ({ name, tripItemIds }) =>
      copyPublicTrip({ name, publicTripId, tripItemIds, regionCode }).then(
        Mapper.fullTrip,
      ),
    ...options,
  })
}
